import * as React from 'react';
import type { AppearanceStoreInstance } from './appearance';
import type { PreferencesService } from './PreferencesService';

export interface PreferencesContextValue {
  service: PreferencesService;
  appearanceStore: AppearanceStoreInstance;
  /** Themes the admin has approved for user selection. Empty = all available. */
  allowedThemes?: string[];
  /** Accent colors the admin has approved. Empty = all available. */
  allowedAccentColors?: string[];
  /** Whether users are allowed to change the accent color at all. */
  allowAccentColorSelection?: boolean;
}

const PreferencesContext = React.createContext<PreferencesContextValue | null>(null);

export interface PreferencesProviderProps {
  service: PreferencesService;
  appearanceStore: AppearanceStoreInstance;
  allowedThemes?: string[];
  allowedAccentColors?: string[];
  allowAccentColorSelection?: boolean;
  children: React.ReactNode;
}

export function PreferencesProvider({
  service,
  appearanceStore,
  allowedThemes,
  allowedAccentColors,
  allowAccentColorSelection,
  children,
}: PreferencesProviderProps) {
  const value = React.useMemo(
    () => ({
      service,
      appearanceStore,
      allowedThemes,
      allowedAccentColors,
      allowAccentColorSelection,
    }),
    [service, appearanceStore, allowedThemes, allowedAccentColors, allowAccentColorSelection],
  );
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const ctx = React.useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used inside <PreferencesProvider>');
  return ctx;
}
